// src/hooks/useLoans.ts
import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import toast from 'react-hot-toast';

// ────────────────────────────────────────────────
// Tipagens
// ────────────────────────────────────────────────
export interface Loan {
  _id: string;
  client: {
    _id: string;
    name: string;
    phone?: string;
    email?: string;
  };
  loanAmountRequested: number;
  loanAmountApproved?: number;
  purpose: string;
  termUnit?: 'Dias' | 'Meses';
  termDays?: number;
  termMonths: number;
  interestRate: number;
  interestType?: 'Simples' | 'Composto' | 'Flat' | 'Decrescente';
  paymentFrequency: 'Diário' | 'Semanal' | 'Quinzenal' | 'Mensal' | 'Trimestral';
  gracePeriodDays: number;
  guaranteeType: string;
  guaranteeDescription?: string;
  guaranteeValue?: number;
  guarantors?: Array<{ name: string; identification: string; phone?: string }>;
  approvalStatus: 'Pendente' | 'Aprovado' | 'Rejeitado' | 'Cancelado';
  disbursementDate?: string;
  firstPaymentDate?: string;
  totalPaid: number;
  outstandingBalance: number;
  daysOverdue: number;
  lastPaymentDate?: string;
  payments?: Array<{
    date: string;
    amount: number;
    method: string;
    notes?: string;
    receivedBy?: { firstName: string; lastName: string };
  }>;
  approvedBy?: { firstName: string; lastName: string };
  approvalDate?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface LoanFilters {
  status?: 'Pendente' | 'Aprovado' | 'Rejeitado' | 'Em curso' | 'Atrasado' | 'Quitado';
  clientId?: string;
  overdue?: boolean;
  page?: number;
  limit?: number;
}

// Evento global para sinalizar mudanças na lista de créditos
export const LOANS_CHANGED_EVENT = 'loansChanged';
const notifyLoansChanged = () => window.dispatchEvent(new Event(LOANS_CHANGED_EVENT));

// ────────────────────────────────────────────────
// Hooks de Leitura
// ────────────────────────────────────────────────

/** Lista todos os microcréditos com filtros */
export const useLoans = (filters: LoanFilters = {}) => {
  const [data, setData] = useState<Loan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.overdue) params.append('overdue', 'true');
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const res = await api.request<any>(`/loans${queryString}`);
      setData(Array.isArray(res) ? res : res?.loans || []);
    } catch (err: any) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    load();
    window.addEventListener(LOANS_CHANGED_EVENT, load);
    return () => window.removeEventListener(LOANS_CHANGED_EVENT, load);
  }, [load]);

  return { data, isLoading, error, refetch: load };
};

/** Detalhe de um microcrédito específico */
export const useLoan = (loanId: string | undefined) => {
  const [data, setData] = useState<Loan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!loanId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.request<Loan>(`/loans/${loanId}`);
      setData(res);
    } catch (err: any) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [loanId]);

  useEffect(() => {
    load();
    window.addEventListener(LOANS_CHANGED_EVENT, load);
    return () => window.removeEventListener(LOANS_CHANGED_EVENT, load);
  }, [load]);

  return { data, isLoading, error, refetch: load };
};

// ────────────────────────────────────────────────
// Hooks de Escrita (Mutations)
// ────────────────────────────────────────────────

const useLoanMutation = <T,>(
  mutationFn: (data: T) => Promise<any>,
  successMessage: string
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = useCallback(
    async (data: T) => {
      setIsLoading(true);
      setError(null);
      try {
        await mutationFn(data);
        toast.success(successMessage);
        notifyLoansChanged();
      } catch (err: any) {
        toast.error(err?.message || 'Ocorreu um erro');
        setError(err);
        console.error(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [mutationFn, successMessage]
  );

  const mutate = useCallback(
    (data: T, options?: { onSuccess?: () => void }) => {
      mutateAsync(data).then(() => options?.onSuccess?.());
    },
    [mutateAsync]
  );

  return { mutate, mutateAsync, isLoading, error };
};

/** Criar novo pedido de microcrédito */
export const useCreateLoan = () => {
  return useLoanMutation<Partial<Loan>>(
    (data) =>
      api.request('/loans', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    'Pedido de crédito registado com sucesso!'
  );
};

/** Aprovar microcrédito */
export const useApproveLoan = (loanId: string) => {
  return useLoanMutation<{ loanAmountApproved?: number; notes?: string }>(
    (data) =>
      api.request(`/loans/${loanId}/approve`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    'Crédito aprovado com sucesso!'
  );
};

/** Rejeitar microcrédito */
export const useRejectLoan = (loanId: string) => {
  return useLoanMutation<{ reason?: string }>(
    (data) =>
      api.request(`/loans/${loanId}/reject`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    'Crédito rejeitado'
  );
};

/** Registar desembolso */
export const useDisburseLoan = (loanId: string) => {
  return useLoanMutation<undefined>(
    () => api.request(`/loans/${loanId}/disburse`, { method: 'PATCH' }),
    'Desembolso registado com sucesso!'
  );
};

/** Registar pagamento / prestação */
export const useRegisterPayment = (loanId: string) => {
  return useLoanMutation<{
    amount: number;
    paymentDate?: string;
    method?: string;
    notes?: string;
  }>(
    (data) =>
      api.request(`/loans/${loanId}/payments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    'Pagamento registado com sucesso!'
  );
};

/** Forçar atualização de atrasos (admin) */
export const useUpdateOverdue = () => {
  return useLoanMutation<undefined>(
    () => api.request('/loans/update-overdue', { method: 'POST' }),
    'Atrasos atualizados com sucesso'
  );
};

export default {
  useLoans,
  useLoan,
  useCreateLoan,
  useApproveLoan,
  useRejectLoan,
  useDisburseLoan,
  useRegisterPayment,
  useUpdateOverdue,
};